import { ASSET_CREATOR_USER_ID } from '~/constants';

import { type UUID } from '~/types/common';
import { type User } from '~/types/user';

import { DELIVERY_NOTE_EVENT_DISPLAY } from '~/modules/delivery-notes/view/constants';

import { Icon } from '@vestigas/ui';

import { type DeliveryNoteEvent, type DeliveryNoteEventName } from '../types';

import { getCreatorId } from './getCreatorId';

/**
 * Enhances an accepted item with data from the matching line item from the delivery note.
 * @param acceptedItem - The accepted item to enhance.
 * @param lineItems - The line items data from the delivery note.
 * @returns The enhanced accepted item.
 */
const mergeLineItemWithAcceptedItems = (
  acceptedItem: any,
  lineItems: any[],
) => {
  if (!acceptedItem || !lineItems?.length) {
    return acceptedItem;
  }

  const lineItem = lineItems.find(
    (lineItem) => lineItem.lineItemIndex === acceptedItem.lineItemIndex,
  );

  return {
    ...acceptedItem,
    lineItem,
  };
};

type P = {
  assetChainEnhanced: any;
  deliveryNoteEnhanced: any;
  getUser: (userId: UUID) => User | undefined;
};

/**
 * Extracts the event from an asset chain.
 * @param assetChainEnhanced - The enhanced version of the asset chain to extract the event from.
 * @param lineItems - The enhanced version of the lineItems.
 * @param getUser - A function to get the user from the user ID.
 * @returns The event.
 */
export const getEventFromAssetChain = ({
  assetChainEnhanced,
  lineItems,
  getUser,
}: P): DeliveryNoteEvent | undefined => {
  if (!assetChainEnhanced?.assetChainContext?.meta) {
    return undefined;
  }

  const {
    createdOn,
    createdBy,
    calculatedAcceptState,
    assetChainContext: {
      assetId,
      assetVersion,
      attachments,
      context,
      idPrevItem,
      meta,
      signatureRole,
    },
    issuer,
    technicalIssuer,
  } = assetChainEnhanced;

  const timestamp = createdOn;
  const creatorId = getCreatorId(createdBy);
  const user =
    creatorId && typeof getUser === 'function' ? getUser(creatorId) : undefined;
  if (Boolean(user) && creatorId === ASSET_CREATOR_USER_ID) {
    // if the asset creator is the Vestigas system user, set the technical issuer (fallback: issuer) as company
    const assetCreatorCompany = technicalIssuer ?? issuer;

    user.companyId = assetCreatorCompany?.id;
    user.companyName = assetCreatorCompany?.legalOrganization?.name;
  }

  const commonData = { deliveryNoteId: assetId };

  const getIcon = (eventName: DeliveryNoteEventName) => {
    const metaphor = DELIVERY_NOTE_EVENT_DISPLAY({})[eventName]?.icon;

    if (!metaphor) {
      return undefined;
    }

    return <Icon metaphor={metaphor} size="sm" />;
  };

  // Handle 'accepted' events
  if (meta?.action?.accepted) {
    const acceptedItems = assetChainEnhanced?.acceptedItems
      ?.filter(({ compositeAcceptState }) =>
        ['accepted', 'partiallyAccepted'].includes(compositeAcceptState),
      )
      .map((acceptedItem) =>
        mergeLineItemWithAcceptedItems(acceptedItem, lineItems),
      );
    const rejectedItems = assetChainEnhanced?.acceptedItems
      ?.filter(({ compositeAcceptState }) =>
        ['rejected'].includes(compositeAcceptState),
      )
      .map((acceptedItem) =>
        mergeLineItemWithAcceptedItems(acceptedItem, lineItems),
      );

    const acceptedEventData = {
      ...commonData,
      acceptedItems,
      attachments,
      rejectedItems,
      signatureRole,
    };

    if (calculatedAcceptState === 'rejected') {
      return {
        data: acceptedEventData,
        eventCategory: 'processStateChange',
        eventName: 'deliveryNoteRejected',
        icon: getIcon('deliveryNoteRejected'),
        timestamp,
        user,
      };
    }

    return {
      data: acceptedEventData,
      eventCategory: 'processStateChange',
      eventName: 'deliveryNoteAccepted',
      icon: getIcon('deliveryNoteAccepted'),
      timestamp,
      user,
    };
  }

  // Handle 'arrived' events
  if (meta?.action?.arrived || meta?.arrived) {
    return {
      data: {
        ...commonData,
        ...(meta?.action?.arrived ?? meta?.arrived ?? {}),
      },
      eventCategory: 'processStateChange',
      eventName: 'deliveryNoteArrived',
      icon: getIcon('deliveryNoteArrived'),
      timestamp,
      user,
    };
  }

  // Handle 'cancelled' events
  if (meta?.action?.cancelled) {
    return {
      data: {
        ...commonData,
        ...(meta?.action?.cancelled ?? {}), // TODO: get details?
      },
      eventCategory: 'processStateChange',
      eventName: 'deliveryNoteCancelled',
      icon: getIcon('deliveryNoteCancelled'),
      timestamp,
      user,
    };
  }

  // Handle 'archived' / 'unarchived' events
  if (![null, undefined].includes(meta?.action?.archived)) {
    if (meta?.action?.archived) {
      return {
        data: {
          ...commonData,
          ...(meta?.action?.archived ?? {}),
        },
        eventCategory: 'processStateChange',
        eventName: 'deliveryNoteArchived',
        icon: getIcon('deliveryNoteArchived'),
        timestamp,
        user,
      };
    }

    return {
      data: {
        ...commonData,
        ...(meta?.action?.archived ?? {}),
      },
      eventCategory: 'processStateChange',
      eventName: 'deliveryNoteUnarchived',
      icon: getIcon('deliveryNoteUnarchived'),
      timestamp,
      user,
    };
  }

  // Handle 'shared' events
  if (Object.keys(meta?.share ?? {}).length) {
    return {
      data: {
        ...commonData,
        ...(meta?.share ?? {}), // TODO: get user details from sgw items?
      },
      eventCategory: 'processStateChange',
      eventName: 'deliveryNoteShared',
      icon: getIcon('deliveryNoteShared'),
      timestamp,
      user,
    };
  }

  // Handle 'assigned' events
  if (meta?.action?.assign) {
    return {
      data: {
        ...commonData,
        ...(meta?.action?.assign ?? {}), // TODO: get user details from sgw items?
      },
      eventCategory: 'processStateChange',
      eventName: 'deliveryNoteAssigned',
      icon: getIcon('deliveryNoteAssigned'),
      timestamp,
      user,
    };
  }

  // Handle 'created' / 'edited' events; 'edited' is also the default fallback event type
  const acceptedItems = assetChainEnhanced?.lineItems?.filter((lineItem) =>
    ['accepted', 'partiallyAccepted'].includes(
      lineItem?.acceptState?.[
        assetChainEnhanced?.assetChainContext?.signatureRole
      ]?.compositeAcceptState,
    ),
  );
  const rejectedItems = assetChainEnhanced?.lineItems?.filter((lineItem) =>
    ['rejected'].includes(
      lineItem?.acceptState?.[
        assetChainEnhanced?.assetChainContext?.signatureRole
      ]?.compositeAcceptState,
    ),
  );

  const createEditEventData = {
    ...commonData,
    acceptedItems,
    attachments,
    rejectedItems,
  };

  if (assetVersion === 1) {
    return {
      data: createEditEventData,
      eventCategory: 'processStateChange',
      eventName: 'deliveryNoteCreated',
      icon: getIcon('deliveryNoteCreated'),
      timestamp,
      user,
    };
  }

  return {
    data: createEditEventData,
    eventCategory: 'processStateChange',
    eventName: 'deliveryNoteEdited',
    icon: getIcon('deliveryNoteEdited'),
    timestamp,
    user,
  };
};
